import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Main } from '../components/layouts/Layouts';
import Spinner from '../components/Spinner';
import Pagination from '../components/Pagination';
import { apiFetch } from '../api/apiFetch';

const getLabelText = (result) => {
  if (result.type === 'Field Note') return 'FIELD NOTES';
  if (result.type === 'Playbook') {
    return result.subType
      ? `PLAYBOOK / ${result.subType.toUpperCase()}`
      : 'PLAYBOOK';
  }
  return result.type.toUpperCase();
};

const filterOptions = [
  { label: 'All Types', value: 'All' },
  { label: 'Playbook', value: 'Playbook' },
  { label: 'Field Notes', value: 'Field Note' },
];

const sortOptions = [
  { label: 'Publish Date', value: 'date' },
  { label: 'Category Type', value: 'type' },
  { label: 'Alphabetical', value: 'title' }
];

const SearchResults = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') || '';

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [filterBy, setFilterBy] = useState('All');
  const [sortBy, setSortBy] = useState('date');

  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [q, filterBy, sortBy]);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    const queryTerm = q.trim();

    apiFetch({
      url: `/api/v2/search?q=${encodeURIComponent(queryTerm)}&sort=${sortBy}&type=${filterBy}`
    })
      .then((data) => {
        setResults(data.results || []);
        setLoading(false);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching search results:', err);
        setResults([]);
        setLoading(false);
      });
  }, [q, sortBy, filterBy]);

  const itemsPerPage = 20;
  const totalPages = Math.ceil(results.length / itemsPerPage);

  const paginatedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return results.slice(startIndex, startIndex + itemsPerPage);
  }, [results, currentPage]);

  const filterLabel =
    filterOptions.find((o) => o.value === filterBy)?.label || 'All Types';
  const sortLabel =
    sortOptions.find((o) => o.value === sortBy)?.label || 'Publish Date';

  return (
    <div className="flex flex-col container py-8 w-full">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center text-middle-gray hover:text-charcoal text-xxs font-extrabold tracking-wide uppercase bg-transparent border-none cursor-pointer p-0 mb-4 outline-none focus:outline-none self-start"
      >
        &lt; Return
      </button>

      {loading && (
        <div className="relative w-full" style={{ height: '300px' }}>
          <Spinner />
        </div>
      )}

      {!loading && (
        <>
          <div
            className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray pb-6 mb-8"
          >
            <h1 className="text-2xl lg:text-3xl font-extrabold text-charcoal">
              {results.length} results for &ldquo;{q}&rdquo;
            </h1>

            <div className="flex items-center gap-3">
              {/* Filter Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                  className="flex items-center px-2 py-2 border border-gray bg-white hover:bg-white-faint text-sm text-charcoal font-medium cursor-pointer transition-colors shadow-sm outline-none focus:outline-none"
                  style={{ borderRadius: '10px' }}
                >
                  Filter By: {filterLabel}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 ml-2 text-charcoal"
                  >
                    <path
                      d="M1.5 2.5H14.5L9.5 8.5V13.5L6.5 14.5V8.5L1.5 2.5Z"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {isFilterDropdownOpen && (
                  <>
                    <button
                      type="button"
                      className="fixed z-20 cursor-default bg-transparent border-none p-0 outline-none"
                      style={{ top: 0, right: 0, bottom: 0, left: 0 }}
                      onClick={() => setIsFilterDropdownOpen(false)}
                      aria-label="Close filter menu"
                    />
                    <div
                      className="absolute right-0 mt-2 w-48 bg-white border border-gray rounded-xl shadow-lg z-30 py-1.5"
                      style={{ borderRadius: '12px' }}
                    >
                      {filterOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setFilterBy(option.value);
                            setIsFilterDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors cursor-pointer hover:bg-white-faint ${
                            filterBy === option.value
                              ? 'font-bold text-link-purple bg-purple-100/30'
                              : 'text-charcoal'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Sort Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                  className="flex items-center px-2 py-2 border border-gray bg-white hover:bg-white-faint text-sm text-charcoal font-medium cursor-pointer transition-colors shadow-sm outline-none focus:outline-none"
                  style={{ borderRadius: '10px' }}
                >
                  Sort By: {sortLabel}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 ml-2 text-charcoal"
                  >
                    <path
                      d="M4.5 2.5V13.5M4.5 13.5L1.5 10.5M4.5 13.5L7.5 10.5M11.5 13.5V2.5M11.5 2.5L8.5 5.5M11.5 2.5L14.5 5.5"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {isSortDropdownOpen && (
                  <>
                    <button
                      type="button"
                      className="fixed z-20 cursor-default bg-transparent border-none p-0 outline-none"
                      style={{ top: 0, right: 0, bottom: 0, left: 0 }}
                      onClick={() => setIsSortDropdownOpen(false)}
                      aria-label="Close sort menu"
                    />
                    <div
                      className="absolute right-0 mt-2 w-48 bg-white border border-gray rounded-xl shadow-lg z-30 py-1.5"
                      style={{ borderRadius: '12px' }}
                    >
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setSortBy(option.value);
                            setIsSortDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors cursor-pointer hover:bg-white-faint ${
                            sortBy === option.value
                              ? 'font-bold text-link-purple bg-purple-100/30'
                              : 'text-charcoal'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            {paginatedResults.length > 0 ? (
              paginatedResults.map((item) => (
                <ResultCard key={item.id} item={item} />
              ))
            ) : (
              <div
                className="text-center py-16 bg-white-faint border border-gray rounded-xl"
                style={{ borderRadius: '12px' }}
              >
                <p className="text-middle-gray text-base">
                  No results found matching your search term.
                </p>
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-8">
                <Pagination
                  currentPage={currentPage}
                  totalPage={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const ResultCard = ({ item }) => {
  const label = getLabelText(item);

  return (
    <div
      className="bg-white border border-gray rounded-xl p-6 transition-all duration-200 hover:shadow-md hover:border-gray-dark"
      style={{ borderRadius: '12px' }}
    >
      <div className="flex flex-col lg:flex-row items-center gap-6">
        {item.imageUrl && (
          <div
            className="flex justify-center min-w-1/5 xl:max-w-1/5 overflow-hidden rounded-lg"
          >
            <img src={item.imageUrl} alt="" />
          </div>
        )}
        <div className="flex flex-col justify-between h-full">
          <div>
            <span
              className="text-xxs font-extrabold uppercase text-link-purple tracking-wider mb-2 block"
            >
              {label}
            </span>

            {item.isExternal ? (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline block"
              >
                <CardTitle title={item.title} />
              </a>
            ) : (
              <Link to={item.url} className="no-underline block">
                <CardTitle title={item.title} />
              </Link>
            )}

            <div
              className="text-sm text-charcoal font-sans leading-relaxed line-clamp-2"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: item.description
              }}
            />
          </div>

          {item.date ? (
            <span className="text-xs text-middle-gray font-sans mt-3 block">
              {item.date}
            </span>
          ) : (
            <div className="mt-3 flex justify-start">
              <Link
                to={item.url}
                className="text-xs font-bold text-link-purple hover:underline"
              >
                View in Playbook &rarr;
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CardTitle = ({ title }) => (
  <h3
    className="text-lg lg:text-xl font-extrabold text-charcoal leading-tight mb-2 hover:text-link-purple transition-colors"
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{ __html: title }}
  />
);

// const options = {
//   layout: 'full',
//   content: <SearchResultsContent />,
//   wrapperClasses: 'flex justify-center',
//   innerStyle: { width: '100%', maxWidth: '1000px' }
// };

// const SearchResults = () => <Main options={options} />;

export default SearchResults;
